import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ChevronRight } from "lucide-react";
import { useAuth } from "../lib/auth";

/**
 * Persistent sticky banner shown to unverified agents on every app page.
 * "Verified" = the four Brokerage & Licensing fields are all filled
 * (Agent.is_verified server-side, mirrored in AgentOut).
 *
 * Clicking the banner deep-links to Settings#brokerage — the section
 * anchor is handled by Settings.jsx which auto-scrolls on hash.
 * Auto-hides itself on the Settings page (Where user is already
 * completing the profile) to avoid nagging while they work.
 */
export default function VerificationBanner() {
  const { agent } = useAuth();
  const location = useLocation();

  // Nothing to gate on until agent is loaded and we know their state.
  if (!agent) return null;
  // Once verified, the banner is a no-op.
  if (agent.is_verified) return null;
  // Don't nag on the settings page — they're already there.
  if (location.pathname.startsWith("/settings")) return null;

  return (
    <Link
      to="/settings#brokerage"
      role="alert"
      aria-live="polite"
      data-testid="verification-banner"
      className="sticky top-0 z-40 block w-full text-white shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-amber-300"
      style={{ background: "linear-gradient(90deg, #B45309 0%, #D97706 100%)" }}
    >
      <div className="mx-auto max-w-7xl 2xl:max-w-[1600px] px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-amber-100" aria-hidden="true" />
        <span className="text-xs sm:text-sm font-semibold flex-1 min-w-0 truncate">
          <span className="hidden sm:inline">Action required — </span>
          Complete your Brokerage &amp; Licensing profile to unlock all features.
        </span>
        <span className="hidden sm:inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-amber-100 hover:text-white">
          Complete now
          <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </span>
        <ChevronRight className="sm:hidden h-4 w-4 text-amber-100 flex-shrink-0" aria-hidden="true" />
      </div>
    </Link>
  );
}
